// @ts-nocheck
import { loadUnitsCatalog } from './loadUnitsCatalog';
import { resolveMaterialId, resolveUnitId } from './normalizeUnitString';
import { roundQuantity } from './roundQuantity';
import type {
  ConvertQuantityOptions,
  ConvertQuantityResult,
  UnitDefinition,
  UnitDimension,
} from './types';

const US_CUP_ML = 236.588;

function passthrough(value: number, fromUnitRaw: string, fromUnitId: string | null): ConvertQuantityResult {
  return {
    ok: true,
    value,
    unit: fromUnitId ?? fromUnitRaw,
    converted: false,
  };
}

function convertSameDimension(
  value: number,
  from: UnitDefinition,
  to: UnitDefinition,
  decimals: number,
): number {
  const baseValue = value * from.factorToBase;
  const converted = baseValue / to.factorToBase;
  return roundQuantity(converted, from.dimension, decimals);
}

function volumeToGrams(value: number, unit: UnitDefinition, gramsPerUsCup: number): number {
  const ml = value * unit.factorToBase;
  return (ml / US_CUP_ML) * gramsPerUsCup;
}

function gramsToVolume(valueGrams: number, unit: UnitDefinition, gramsPerUsCup: number): number {
  const ml = (valueGrams / gramsPerUsCup) * US_CUP_ML;
  return ml / unit.factorToBase;
}

function convertViaMaterial(
  value: number,
  from: UnitDefinition,
  to: UnitDefinition,
  gramsPerUsCup: number,
  decimals: number,
): number {
  let grams: number;
  if (from.dimension === 'volume') {
    grams = volumeToGrams(value, from, gramsPerUsCup);
  } else {
    grams = value * from.factorToBase;
  }

  let result: number;
  if (to.dimension === 'volume') {
    result = gramsToVolume(grams, to, gramsPerUsCup);
  } else {
    result = grams / to.factorToBase;
  }

  return roundQuantity(result, to.dimension, decimals);
}

export function convertQuantity(options: ConvertQuantityOptions): ConvertQuantityResult {
  const catalog = loadUnitsCatalog();
  const locale = options.locale ?? 'en';
  const fromUnitId = resolveUnitId(options.fromUnit, catalog, locale);
  const toUnitId = resolveUnitId(options.toUnit, catalog, locale);

  if (!fromUnitId || !toUnitId) {
    return passthrough(options.value, options.fromUnit, fromUnitId);
  }

  const fromUnit = catalog.unitsById.get(fromUnitId);
  const toUnit = catalog.unitsById.get(toUnitId);
  if (!fromUnit || !toUnit) {
    return passthrough(options.value, options.fromUnit, fromUnitId);
  }

  const decimals = catalog.rounding[toUnit.dimension]?.decimals ?? 2;

  if (fromUnit.dimension === toUnit.dimension) {
    return {
      ok: true,
      value: convertSameDimension(options.value, fromUnit, toUnit, decimals),
      unit: toUnitId,
      converted: true,
    };
  }

  const isVolumeWeightPair =
    (fromUnit.dimension === 'volume' && toUnit.dimension === 'weight')
    || (fromUnit.dimension === 'weight' && toUnit.dimension === 'volume');

  if (!isVolumeWeightPair) {
    return passthrough(options.value, options.fromUnit, fromUnitId);
  }

  if (!options.material) {
    return passthrough(options.value, options.fromUnit, fromUnitId);
  }

  const materialId = resolveMaterialId(options.material, catalog);
  if (!materialId) {
    return passthrough(options.value, options.fromUnit, fromUnitId);
  }

  const material = catalog.materialsById.get(materialId);
  if (!material) {
    return passthrough(options.value, options.fromUnit, fromUnitId);
  }

  return {
    ok: true,
    value: convertViaMaterial(
      options.value,
      fromUnit,
      toUnit,
      material.gramsPerUsCup,
      decimals,
    ),
    unit: toUnitId,
    converted: true,
  };
}

/** Expression-facing helper: returns number only, passthrough value on failure. */
export function convertQuantityValue(options: ConvertQuantityOptions): number {
  const result = convertQuantity(options);
  return result.converted ? result.value : options.value;
}

export function resolveDimension(value: string): UnitDimension | null {
  const catalog = loadUnitsCatalog();
  const unitId = resolveUnitId(value, catalog);
  if (!unitId) {
    return null;
  }
  return catalog.unitsById.get(unitId)?.dimension ?? null;
}
